(ns minorodata.components.transformations.transforms
  (:require
   ["allotment" :refer [Allotment]]
   ["date-fns" :refer [differenceInMilliseconds]]
   ["humanize-duration" :as humanizeDuration]
   ["react" :as react]
   [lucide-react :refer [Calendar Check ChevronDown Code FileEdit FileMinus
                         FilePlus MoreHorizontal PlayCircle Plus TriangleAlert
                         UnfoldVertical User Wand2]]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.utils :as utils]
   [minorodata.components.infinite-scroll.core :as c.infinite-scroll]
   [minorodata.components.monaco.diff-editor :as monaco.diff-editor]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.transformations.modals :refer [group-create-dialog]]
   [minorodata.components.transformations.utils :refer [create-and-edit
                                                        move-transform-submenu
                                                        transform-edit-sql-button
                                                        transform-more-options-button]]
   [minorodata.utils :as minoro-utils]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [ui.accordion :refer [accordion-content accordion-item accordion-root
                         accordion-trigger]]
   [ui.button :refer [button]]
   [ui.chip :refer [chip]]
   [ui.date-time :refer [date-text date-tooltip]]
   [ui.dialog :refer [dialog dialog-actions dialog-content dialog-description
                      dialog-root dialog-title]]
   [ui.dropdown-menu :refer [dropdown-menu-checkbox dropdown-menu-content
                             dropdown-menu-item dropdown-menu-root
                             dropdown-menu-trigger]]
   [ui.fields :as fields]
   [ui.table :refer [resizable-th sort-label table tbody td th thead tr]]
   [ui.table-widths :refer [column-widths]]
   [ui.tooltip :as tooltip]
   [ui.typography :as typography :refer [h2 h3 small]]
   [ui.utils :refer [clsx]]))

(defn fetch-transforms [group-id replace?]
  (dispatch [:environments.api/fetch-transforms {:group-id group-id :replace? replace?}]))

(defn fetch-more-transforms []
  (let [panel       @(re-posh/subscribe [:transforms-panel/pull])
        group-panel @(re-posh/subscribe [:groups-panel/pull '[{:groups-panel/active-group [:group/id]}]])
        group-id (get-in group-panel [:groups-panel/active-group :group/id])]
    (dispatch [:environments.api/fetch-transforms {:group-id group-id
                                                   :page (inc (:transforms-panel/page panel))}])))

(defn- truncated-text [text]
  [:div {:class "truncate" :title text} text])

(defn schedule-display
  [{:keys [schedule unscheduled? upstream-deps? group-scheduled? paused?]}]
  [:div {:class "flex flex-col min-w-0"}
   (cond
     group-scheduled?
     [truncated-text "Group based"]

     (and unscheduled? upstream-deps?)
     [truncated-text "Dependency based"]

     (not unscheduled?)
     (if (= "No schedule" schedule)
       [truncated-text schedule]
       [tooltip/tooltip {:content schedule}
        [:div {:class "truncate cursor-help"} schedule]])

     :else
     [tooltip/tooltip {:content "This transform wouldn't run. It hasn't got any upstream dependencies in the transforms and needs to be scheduled."}
      [typography/p {:class "truncate"} "Not scheduled"]])

   (when paused?
     [typography/p {:class "text-kleene-error-01"}
      "Paused"])])

(defn select-launch-option [{:keys [transform-id master-id option-id environment-id]}]
  (dispatch [:transform.api/start-by-id {:transform-id transform-id :master-id master-id :type option-id :environment-id environment-id}]))



(def launch-options-prod
  [{:id    :upstream
    :title "Run upstream"}
   {:id    :downstream
    :title "Run downstream"}
   {:id    :single
    :title "Run this one"}])

(def launch-options-env
  [{:id    :downstream-in-environment
    :title "Run downstream"}
   {:id    :single-in-environment
    :title "Run this one"}])


(def status-names
  {"succeeded" "Finished"
   "pending"   "Running..."
   "failed"    "Failed"

   "completed" "Finished"

   "skipped"   "Skipped"

   "default"   "Never executed"})

(def status-colors
  {"succeeded" "text-kleene-green"
   "completed" "text-kleene-green"
   "pending"   "text-kleene-text-warning"
   "failed"    "text-kleene-error-01"

   "default"   "text-kleene-text-secondary"})

(def status-bg-colors
  {:success "text-kleene-green"
   :info    "text-kleene-green animate-kleene-pulse"
   :error   "text-kleene-error-01"})

(defn execution-time
  "Status label, last-run time and duration; `plain?` skips the Radix date tooltip."
  [{:keys [last-run class plain?]}]
  (let [{:keys [status completed-at finished-at started-at]} last-run
        completed-at   (or completed-at finished-at)
        execution-time (differenceInMilliseconds (js/Date. completed-at) (js/Date. started-at))
        date-component (if plain? date-text date-tooltip)]

    [small {:class (clsx "block" (get status-colors (:status last-run) (get status-colors "default")) class)}
     (get status-names (:status last-run) (get status-names "default"))
     (when (and completed-at (not= status "skipped"))
       [:<>
        " "
        [date-component {:date completed-at}]
        ;; extracts report a run date but no start time, so there is no duration
        (when started-at
          (str
           " in "
           (humanizeDuration
            execution-time
            #js {:round (> execution-time 501)})))])]))

(defn get-status [statuses transform new-endpoint?]
  (let [status-key (if new-endpoint? :entity-id :transform-id)
        id-key     (if new-endpoint? :master-id :id)]
    (minoro-utils/find-by-field statuses status-key (get transform id-key))))

(def modification-icons
  {"deleted"           FileMinus
   "directly-modified" FileEdit
   "newly-created"     FilePlus})

(def modification-colors
  {"deleted"           "text-kleene-error-02"
   "directly-modified" "text-kleene-warning-02"
   "newly-created"     "text-kleene-primary-v2"})

(def modification-titles
  {"deleted"           "Will be deleted in Prod"
   "directly-modified" "Edited transform"
   "newly-created"     "New transform"})



(defn- breakable-name [name]
  (interpose [:wbr] (re-seq #"[^_]*_+|[^_]+" (str name))))

(defn transform-name [{:keys [name full-names?]}]
  (let [ref              (react/useRef nil)
        [open? set-open] (react/useState false)]
    (if full-names?
      (into [:div {:class "wrap-anywhere"}] (breakable-name name))
      [tooltip/tooltip {:content        (into [:span] (breakable-name name))
                        :class          "wrap-anywhere"
                        :open           open?
                        :on-open-change #(set-open (and % (let [el (.-current ref)]
                                                            (> (.-scrollWidth el) (.-clientWidth el)))))}
       [:div {:ref ref :class "truncate"} name]])))

(defn transform-table-row [{:keys [transform-id duplicated-schedule? selected on-toggle full-names?]}]
  (let [{:keys [group-id id input-tables manual-deps? master-id modification-state name orphan? paused? schedule
                status-style status-title unscheduled? updated-at updated-by upstream-deps?]
         :as transform}
        @(re-posh/subscribe [:transform.subs/panel-transform transform-id])

        {group-scheduled? :scheduled?}
        @(re-posh/subscribe [:group.subs/panel-group group-id])

        prod-env?     @(re-posh/subscribe [:environments.subs/production-active?])
        deleted?      (= modification-state "deleted")
        env-id        @(re-posh/subscribe [:environments.subs/active-environment-id])
        new-endpoint? @(re-posh/subscribe [:unleash/is-enabled "transform-execution-dependencies"])
        statuses      @(re-posh/subscribe [:transform-status-list/prop :transform-status-list/statuses])
        last-run      (get-status statuses transform new-endpoint?)

        launch-fn #(select-launch-option {:transform-id   transform-id
                                          :master-id      master-id
                                          :option-id      %
                                          :environment-id (when (not prod-env?) env-id)})
        launch-options (if prod-env?
                         launch-options-prod
                         launch-options-env)
        icon       (get modification-icons modification-state)
        icon-color (get modification-colors modification-state)
        icon-title (get modification-titles modification-state)]
    [tr {:class (clsx transform-id "group")
         :data-cy "transform-row"}
     [td {:class "p-0"}
      [fields/checkbox {:checked   selected
                        :class     (clsx "opacity-0 group-hover:opacity-100 transition-opacity"
                                         {"opacity-100" selected})
                        :on-change on-toggle}]]
     (when-not prod-env?
       [td {:no-padding true :class "w-4"}
        [:div {:class (clsx "leading-zero" icon-color)}
         (when icon
           [tooltip/tooltip {:content icon-title}
            [:> icon {:style {:line-height 0}}]])]])
     [td
      [:div {:class "flex justify-between gap-2"}
       [:div {:class "flex-1 min-w-0"}
        [:f> transform-name {:name name :full-names? full-names?}]
        (when prod-env?
          [execution-time {:last-run last-run}])]
       (when manual-deps?
         [:div {:class "shrink-0"}
          [tooltip/tooltip {:content "Manually set dependencies"}
           [:> UnfoldVertical {:size 20 :class "text-kleene-warning-01"}]]])]]
     [td {:class [(when prod-env? "max-[1300px]:hidden")
                  (when-not prod-env?  "max-[1100px]:hidden")]}
      [:p {:class "truncate"} [date-tooltip {:date updated-at}]]
      [:p {:class "truncate"}
       " by " [:span {:title updated-by} updated-by]]]
     (when prod-env?
       [td {:class "max-[1100px]:hidden"}
        [:div {:class "flex gap-2 items-start"}
         [schedule-display
          {:transform-id     id
           :input-tables     input-tables
           :orphan?          orphan?
           :schedule         schedule
           :unscheduled?     unscheduled?
           :upstream-deps?   upstream-deps?
           :group-scheduled? group-scheduled?
           :paused?          paused?}]
         (when duplicated-schedule?
           [tooltip/tooltip {:content "To ensure transforms with the same schedule are launched together, place them in a separate group, and schedule the group instead."}
            [:> TriangleAlert {:size 20 :class "shrink-0 text-kleene-warning-01"}]])]])

     [td {:class "text-right"}
      [:div {:class "flex gap-2 items-center"}
       [dropdown-menu-root
        [dropdown-menu-trigger {:data-cy  "transform-run-btn"
                                :disabled deleted?
                                :title    "Run transform"}
         (if status-title
           [tooltip/tooltip {:content status-title}
            [:> PlayCircle {:class (clsx "cursor-pointer hover:text-kleene-secondary-v2 transition-colors"
                                         {"text-kleene-text-secondary!" deleted?}
                                         (get status-bg-colors status-style))}]]

           [:> PlayCircle {:class (clsx "cursor-pointer hover:text-kleene-secondary-v2 transition-colors"
                                        {"text-kleene-text-secondary!" deleted?}
                                        (get status-bg-colors status-style))}])]
        (into [dropdown-menu-content]
              (for [{:keys [id title]} launch-options]
                [dropdown-menu-item {:on-click #(launch-fn id)
                                     :data-cy (str "transform-launch-" id "-btn")}
                 title]))]

       [transform-edit-sql-button {:transform transform
                                   :disabled  deleted?}]
       [transform-more-options-button {:transform transform
                                       :disabled  deleted?}]]]]))

(defn sort-transforms [group-id order-field]
  (let [{:transforms-panel/keys [order-by order-dir]} @(re-posh/subscribe [:transforms-panel/pull])
        active? (= order-field order-by)
        next-order-dir (if active?
                         (if (= order-dir :ASC) :DESC :ASC)
                         :ASC)]
    (dispatch [:app.events/set-local-storage "transforms-panel-sort" {:order-by  order-field
                                                                      :order-dir next-order-dir}])
    (dispatch [:environments.api/fetch-transforms {:page      1
                                                   :group-id  group-id
                                                   :order-by  order-field
                                                   :order-dir next-order-dir
                                                   :replace?  true}])))

(def ^:private column-widths-key "transforms-table-columns")

(def ^:private checkbox-width 20)
(def ^:private modification-width 24)
(def ^:private actions-width 112)

(def ^:private name-min-width 240)
(def ^:private name-floor-width 160)
(def ^:private flexible-default-width 200)
(def ^:private flexible-min-width 140)

(defn- visible-flexible-columns [row]
  (->> (js/Array.from (.querySelectorAll row "th[data-flexible]"))
       (filter #(pos? (.-offsetWidth %)))
       count))

(defn transforms-table-head [{:keys [group-id selected on-toggle]}]
  (let [{:transforms-panel/keys [order-by order-dir]}
        @(re-posh/subscribe [:transforms-panel/pull])

        timezone  @(re-posh/subscribe [:app.subs/timezone])
        prod-env? @(re-posh/subscribe [:environments.subs/production-active?])

        row-ref             (react/useRef nil)
        [layout set-layout] (react/useState {:available 0 :flexible 0})
        [widths set-widths] (react/useState #(column-widths column-widths-key {}))

        {:keys [available flexible]} layout
        fixed     (cond-> (+ checkbox-width actions-width)
                    (not prod-env?) (+ modification-width))
        room      (- available fixed)
        max-name  (- room (* flexible flexible-min-width))
        resizable? (pos? flexible)

        name-label [sort-label {:active    (= order-by :name)
                                :direction order-dir
                                :on-click  #(sort-transforms group-id :name)}
                    "Name"]]

    (react/useLayoutEffect
     (fn []
       (let [row       (.-current row-ref)
             container (.-parentElement (.closest row "table"))
             measure   (fn []
                         (let [measured {:available (.-clientWidth container)
                                         :flexible  (visible-flexible-columns row)}]
                           (set-layout #(if (= % measured) % measured))))
             observer  (js/ResizeObserver. measure)]
         (measure)
         (.observe observer container)
         #(.disconnect observer)))
     #js [prod-env?])

    [thead
     [tr {:sticky true
          :ref    row-ref
          :class  "*:top-0 bg-white dark:bg-kleene-dark-02"}
      [th {:class "p-0"
           :style {:width checkbox-width}}
       [fields/checkbox {:checked   selected
                         :on-change on-toggle}]]

      (when-not prod-env?
        [th {:style {:width modification-width}}])

      (if resizable?
        [resizable-th {:id          :name
                       :widths      {:name (get widths :name (- room (* flexible flexible-default-width)))}
                       :min-width   (max name-floor-width (min name-min-width max-name))
                       :max-width   max-name
                       :storage-key column-widths-key
                       :on-resize   set-widths
                       :class       "border-r border-kleene-layout-border"}
         name-label]
        [th name-label])

      [th {:data-flexible true
           :class [(when prod-env? "max-[1300px]:hidden")
                   (when-not prod-env?  "max-[1100px]:hidden")]}
       [sort-label {:active    (= order-by :updated_at)
                    :direction order-dir
                    :on-click  #(sort-transforms group-id :updated_at)}
        [:div {:class "truncate"}
         "Updated"
         [:div {:class "-mt-1 truncate"}
          [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]]

      (when prod-env?
        [th {:data-flexible true
             :class "max-[1100px]:hidden"}
         [sort-label {:active    (= order-by :scheduled)
                      :direction order-dir
                      :on-click  #(sort-transforms group-id :scheduled)}
          [:div {:class "truncate"}
           "Schedule"
           [:div {:class "-mt-1 truncate"}
            [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]])

      [th {:class "text-right"
           :style {:width actions-width}}]]]))

(defn loading-table []
  [:div {:class "absolute inset-0 top-[48px] bg-kleene-background/50 z-10 text-center py-4"}
   [c.spinner/spinner-lucide]])

(defn empty-table-row []
  [tr
   [td {:col-span 4}
    [:div {:class "py-4"}
     "No transforms found"]]])

(defn transform-table-rows [{:keys [transforms duplicated-schedule-ids selected-ids on-toggle full-names?]}]
  [:<>
   (for [{:transform/keys [id]} transforms]
     ^{:key id}
     [transform-table-row {:transform-id id
                           :duplicated-schedule? (some #(= % id) duplicated-schedule-ids)
                           :selected     (some #(= % id) selected-ids)
                           :on-toggle    #(on-toggle id)
                           :full-names?  full-names?}])])

(defn transforms-with-duplicated-schedules [transforms]
  (->> transforms
       (filter :transform/schedule-spec)
       (group-by (comp :cron :transform/schedule-spec))
       (filter #(> (count (second %)) 1))
       (reduce (fn [acc [_ v]]
                 (concat acc (map :transform/id v)))
               [])))

(defn transforms-table-body [{:keys [transforms selected-ids loading? on-toggle full-names?]}]
  [tbody
   (when (and (empty? transforms) (not loading?))
     [empty-table-row])

   [transform-table-rows {:transforms   transforms
                          :selected-ids selected-ids
                          :on-toggle    on-toggle
                          :full-names?  full-names?
                          :duplicated-schedule-ids (transforms-with-duplicated-schedules transforms)}]])

(defn transforms-table [{:keys [group-id selected-ids on-toggle full-names?]}]
  (let [env-id     @(re-posh/subscribe [:environments.subs/active-environment-id])
        ;; for the purposes of filtering, nil is essentially the same as
        ;; production env id
        env-id     (if (= utils/production-env-id env-id)
                     nil
                     env-id)
        transforms     (->> @(re-posh/subscribe [:transform.subs/ordered-transforms])
                            (filter #(= env-id (:transform/environment-id %)))
                            (filter #(= group-id (:transform/group-id %))))
        ids           (mapv :transform/id transforms)
        all-selected? (cond
                        (and (not-empty ids)
                             (= (count selected-ids) (count ids)))
                        true

                        (not-empty selected-ids)
                        "indeterminate")]
    [:div
     [table {:class "table-fixed"}
      [:f> transforms-table-head {:group-id  group-id
                                  :selected  all-selected?
                                  :on-toggle #(on-toggle (if all-selected? [] ids))}]
      [transforms-table-body {:transforms   transforms
                              :selected-ids selected-ids
                              :on-toggle    on-toggle
                              :full-names?  full-names?}]]]))

(defn create-button [& [props]]
  (let [props (merge {:size    :icon-md
                      :data-cy "create-transform-btn"
                      :variant :secondary
                      :icon    Plus}
                     props)]
    [button props "Create transform"]))

(defn create-transform-menu [{:keys [group-id]}]
  [dropdown-menu-root
   [dropdown-menu-trigger {:as-child false}
    [create-button {:component :div}]]

   [dropdown-menu-content {:style {:max-width 300}}
    [dropdown-menu-item {:data-cy  "sql-editor-btn"
                         :on-click #(create-and-edit group-id)}
     [:div {:class "flex gap-4 pl-1"}
      [:div [:> Code {:class "text-kleene-green"}]]
      [:div
       [h3 "SQL Editor"]
       [small {:class "text-kleene-text-secondary"} "Standard SQL query editor"]]]]
    [dropdown-menu-item {:data-cy  "sql-generator-btn"
                         :on-click #(dispatch [:app.events/navigate-query {:json-flattening true}])}
     [:div {:class "flex gap-4 pl-1"}
      [:div [:> Wand2 {:class "text-kleene-secondary-v2"}]]
      [:div
       [h3 "SQL Generator"]
       [small {:class "text-kleene-text-secondary"} "Generate SQL with Automated JSON parsing and flattening"]]]]]])

(defn create-transform-button [{:keys [group-id]}]
  (let [warehouse-type               @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
        snowflake?                   (= warehouse-type "snowflake")
        show-json-flattening-button? snowflake?]
    (if show-json-flattening-button?
      [create-transform-menu {:group-id group-id}]
      [create-button {:on-click #(create-and-edit group-id)}])))

(defn show-group-delete-modal []
  (dispatch [:app.events/navigate-query {:delete-group true}]))

(defn show-group-edit-modal []
  (dispatch [:app.events/navigate-query {:edit-group true}]))

(defn group-menu [{:keys [disabled prod-env? full-names? on-full-names-change]}]
  [dropdown-menu-root
   [dropdown-menu-trigger {:asChild  false
                           :disabled disabled}
    [button {:size      :icon
             :variant   :secondary
             :icon      MoreHorizontal
             :disabled  disabled
             :data-cy   "group-more-options-btn"
             :component :div}]]
   [dropdown-menu-content
    [dropdown-menu-checkbox {:icon              Check
                             :checked           full-names?
                             :data-cy           "group-more-options-full-names-btn"
                             :on-checked-change on-full-names-change}
     "Show full names"]
    (when prod-env?
      [:<>
       [dropdown-menu-item {:data-cy  "group-more-options-edit-btn"
                            :class    "pl-7"
                            :on-click show-group-edit-modal}
        "Edit group"]
       [dropdown-menu-item {:data-cy  "group-more-options-delete-btn"
                            :class    "pl-7 text-kleene-text-error"
                            :on-click show-group-delete-modal}
        "Delete group"]])]])

(defn group-schedule [{:keys [group-details]}]
  (let [{:keys [schedulable? schedule-spec]} group-details
        cron (:cron schedule-spec)

        timezone @(re-posh/subscribe [:app.subs/timezone])
        loading? @(re-posh/subscribe [:mold-http/loading? :transform.api/fetch-transforms])]
    (cond
      schedulable?
      [:p {}
       (utils/transform-schedule group-details "Dependency based")
       (when (and cron (not= cron utils/empty-cron))
         (str " (" timezone " time zone)"))]

      loading?
      [c.spinner/spinner-lucide {:size 14 :class "leading-3"}]

      :else
      [:p "Not scheduled"])))

(defn transforms-table-wrapper [props & children]
  (into [:div (update props :class #(clsx "px-5 relative overflow-y-auto pb-kai" %))]
        children))

(defn move-transforms [ids group-id on-success]
  (let [on-success (fn []
                     (dispatch [:transform.events/refresh-panel])
                     (on-success))]
    (dispatch [:transform.events/bulk-move {:transform-ids ids
                                            :group-id      group-id
                                            :on-success    on-success}])))

(defn bulk-action-button [{:keys [selected-ids group-id on-success]}]
  (let [[show-delete set-show-delete] (react/useState false)
        [show-dropdown set-show-dropdown] (react/useState false)
        [show-group-create set-show-group-create] (react/useState false)

        on-delete (fn []
                    (set-show-delete false)
                    (on-success))
        on-move   (fn []
                    (set-show-group-create false)
                    (on-success))]
    [:<>
     [dropdown-menu-root {:open show-dropdown
                          :on-open-change #(set-show-dropdown %)}
      [dropdown-menu-trigger
       [button {:variant   :secondary
                :icon      ChevronDown
                :data-cy   "bulk-action-btn"
                :component :div}
        "Bulk actions"]]
      [dropdown-menu-content

       (when (empty? selected-ids)
         [:div {:class "bg-kleene-warning-05 text-kleene-warning-01 px-1 m-1 rounded-md"}
          [:div {:class "mt-1 p-1"}
           "Select one or more transforms"]])


       [move-transform-submenu {:title        (str "Move " (count selected-ids) " transforms")
                                :on-move      #(move-transforms selected-ids % on-move)
                                :on-new-group #(set-show-group-create true)
                                :disabled     (empty? selected-ids)}]
       [dropdown-menu-item {:data-cy  "bulk-action-move-btn"
                            :class    "text-kleene-text-error"
                            :on-click #(set-show-delete true)
                            :disabled (empty? selected-ids)}
        "Delete " (count selected-ids) " transforms"]]]

     [dialog-root {:open           show-delete
                   :on-open-change (fn [open]
                                     (when-not open (set-show-delete false)))}
      [dialog-content
       [dialog-title {:truncate true}
        "Delete " (count selected-ids) " transforms?"]
       [dialog-description "Are you sure you want to delete these transform?"]
       [:div {:class "mt-4 flex justify-end"}
        [button {:on-click  #(dispatch [:transform.events/bulk-delete
                                        {:transform-ids selected-ids
                                         :group-id      group-id
                                         :on-success    on-delete}])
                 :data-cy   "transform-delete-modal-confirm-btn"
                 :variant   :error}
         "Delete transforms"]]]]

     [group-create-dialog {:open       show-group-create
                           :on-close   #(set-show-group-create false)
                           :on-success #(move-transforms selected-ids % on-move)}]]))

(defn transforms-list []
  (let [prod-env?     @(re-posh/subscribe [:environments.subs/production-active?])
        panel         @(re-posh/subscribe [:groups-panel/pull '[{:groups-panel/active-group [:group/id :group/name]}]])
        group-id      (get-in panel [:groups-panel/active-group :group/id])
        group-name    (get-in panel [:groups-panel/active-group :group/name])
        group-details @(re-posh/subscribe [:group.subs/panel-group-details group-id])

        transforms-loading?     @(re-posh/subscribe [:mold-http/loading? :transform.api/fetch-transforms])
        env-transforms-loading? @(re-posh/subscribe [:mold-http/loading? :environments.api/fetch-environment-transforms])

        loading?   (or transforms-loading? env-transforms-loading?)

        [selected-ids set-selected-ids] (react/useState [])
        [full-names? set-full-names]    (react/useState false)
        on-toggle (fn [id]
                    (if (vector? id)
                      (set-selected-ids id)
                      (set-selected-ids (fn [old-ids]
                                          (sort (utils/toggle old-ids id))))))]

    (react/useEffect
     (fn []
       (set-selected-ids []))
     #js [group-id])

    [:div {:class "flex flex-col overflow-hidden"}
     [:header {:class "px-5 py-4 top-0 border-b flex justify-between items-center bg-kleene-background"}
      [:div
       [h2 {:class "flex gap-2"}
        group-name
        [group-menu {:prod-env?            prod-env?
                     :full-names?          full-names?
                     :on-full-names-change set-full-names}]]

       [group-schedule {:group-details group-details}]]

      [:div {:class "flex gap-2"}
       [:f> bulk-action-button {:selected-ids selected-ids
                                :group-id     group-id
                                :on-success   #(on-toggle [])}]
       [create-transform-button {:group-id group-id}]]]

     [c.infinite-scroll/main
      {:component     transforms-table-wrapper
       :on-scroll-end fetch-more-transforms
       :loading?      loading?
       :loader        [loading-table]}
      [transforms-table {:group-id     group-id
                         :selected-ids selected-ids
                         :on-toggle    on-toggle
                         :full-names?  full-names?}]]]))


(defn fetch-more-transform-versions [master-id]
  (let [{:transform-versions-list/keys [page total-pages]} @(re-posh/subscribe [:transform-versions-list/pull])]
    (dispatch [:transform.api/fetch-transform-versions master-id {:page        (inc page)
                                                                  :total-pages total-pages}])))


(defn update-diff-screen [{:keys [group-id previous]}]
  (dispatch [:transform.events/update-diff-screen group-id previous]))


(defn init-versions-list [master-id transform-id]
  (dispatch-sync [:transform-versions-list/add
                  {:transform-versions-list/current        transform-id
                   :transform-versions-list/page           1
                   :transform-versions-list/items-per-page 50}])
  (dispatch-sync [:transform.api/fetch-transform-versions master-id]))

(defn init-versions-list-with-cleanup [master-id transform-id]
  (init-versions-list master-id transform-id)
  (fn []
    (dispatch-sync [:transform.events/cleanup master-id])))

(defn changelist-field [{:keys [type value]}]
  (case type
    :boolean
    (if value "True" "False")

    :chips
    (into [:div {:class "gap-2 flex flex-row flex-wrap"}]
          (for [v value]
            ^{:key v}
            [chip v]))

    (str "\"" value "\"")))


(defn close-versions-list []
  (dispatch [:transform.events/toggle-version-select-modal false]))

(defn rollback-transform [transform-id selected-version-id on-rollback-ev]
  (dispatch [:transform.events/rollback-transform transform-id selected-version-id on-rollback-ev]))


(defn version-modal-actions [{:keys [id]} {:keys [on-rollback-ev]}]
  (let [transform-id id
        confirm-dialog-open?  (reagent/atom false)
        toggle-confirm-dialog #(swap! confirm-dialog-open? not)]
    (fn []
      (let [{:transform-versions-list/keys [selected]} @(re-posh/subscribe [:transform-versions-list/pull])
            selected-version-id selected
            changelist @(re-posh/subscribe [:transform.subs/transform-metadata-changelist transform-id selected-version-id])]
        [:<>
         [dialog {:open @confirm-dialog-open?
                  :on-open-change toggle-confirm-dialog
                  :title "Transform Rollback"
                  :content-props {:size :medium}
                  :content [:div {}
                            (if (seq changelist)
                              [:<>
                               [:p "This transform version has some metadata changes.
                                   Below is a list of fields that will be changed:"]

                               [:div {:class "py-4"}
                                (doall
                                 (for [{:keys [field type previous current]} changelist]
                                   ^{:key field}
                                   [accordion-root {:collapsible true}
                                    [accordion-item {:value field}

                                     [accordion-trigger {}
                                      [:p {:class "capitalize"} field]]

                                     [accordion-content {}

                                      [:div {:class "flex flex-row gap-2"}
                                       [:div {:class "w-1/2"}
                                        [:div {:class "bg-kleene-error-01/10 p-2"}
                                         [changelist-field
                                          {:type  type
                                           :value current}]]]

                                       [:div {:class "w-1/2"}
                                        [:div {:class "bg-kleene-primary-01/10 p-2"}
                                         [changelist-field
                                          {:type  type
                                           :value previous}]]]]]]]))]

                               [:p {:class "mt-4"}
                                "Please confirm the transform rollback"]]

                              [:p {:class "mt-4"}
                               "Please confirm the transform rollback"])]
                  :actions [:<>
                            [button {:data-cy  "rollback-cancel-btn"
                                     :on-click toggle-confirm-dialog
                                     :variant  :secondary}
                             "Cancel"]

                            [button {:data-cy "rollback-confirm-btn"
                                     :on-click
                                     (fn []
                                       (rollback-transform transform-id selected-version-id on-rollback-ev)
                                       (toggle-confirm-dialog)
                                       (close-versions-list))}
                             "Rollback"]]}]


         [dialog-actions
          {:class "p-4 m-0 border-r border-t border-solid sticky bg-kleene-background bottom-0"}
          [button {:on-click toggle-confirm-dialog
                   :data-cy  "roll-back-init-btn"
                   :disabled (nil? selected-version-id)}
           "Roll back to this version"]]]))))


(defn transform-version-item
  [{:keys [group-id transform-id created-by created-at version-name is-active?]}]
  [:div {:class ["group relative flex flex-col gap-1 border-b border-solid px-4 py-3 cursor-pointer transition-colors"
                 (if is-active?
                   "bg-kleene-selected-background"
                   "hover:bg-kleene-hover-background")]
         :on-click   #(update-diff-screen {:group-id group-id
                                           :previous transform-id})
         :data-cy    "transform-version-item"}

   ;; Version name + active indicator
   [:div {:class "flex items-center justify-between gap-1"}
    [:div {:class "flex items-center gap-2 min-w-0"}
     [typography/h5 {:class "truncate"} version-name]]
    (when is-active?
      [:> Check {:size 16 :class "shrink-0 text-kleene-text-success"}])]

   ;; Metadata: author + created date
   [:div {:class "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-kleene-text-secondary"}
    [:span {:class "flex items-center gap-1.5"}
     [:> User {:size 12 :class "shrink-0"}]
     [:span {:class "truncate"} created-by]]
    [:span {:class "flex items-center gap-1.5"}
     [:> Calendar {:size 12 :class "shrink-0"}]
     [date-tooltip {:date created-at}]]]])


(defn transform-versions-list [{:keys [master-id id group-id] :as transform} on-rollback-ev]
  (react/useEffect
   (fn []
     (init-versions-list-with-cleanup master-id id))
   #js [])

  (let [versions   @(re-posh/subscribe [:transform.subs/transform-versions master-id])
        searching? @(re-posh/subscribe [:mold-http/loading? :transform.api/fetch-transform-versions])
        {:transform-versions-list/keys [selected]} @(re-posh/subscribe [:transform-versions-list/pull])
        selected-version-id selected]
    [:div {:class "h-full flex flex-col"}
     (if (and (empty? versions)
              (not searching?))
       [:div {:class "h-full p-4 border-r border-solid"}
        [:p "No transform versions found."]]

       (into
        [c.infinite-scroll/main
         {:component       "div"
          :component-props {:class "border-r border-solid"}
          :loader          [:div {:class "w-full h-full flex justify-center items-center"}
                            [c.spinner/spinner-lucide]]
          :loading?        searching?
          :on-scroll-end   (partial fetch-more-transform-versions master-id)}]
        (for [{:keys [transform-id created-by created-at version-name]} versions
              :let [is-active? (= transform-id selected-version-id)]]
          ^{:key (str transform-id)}
          [transform-version-item {:group-id     group-id
                                   :transform-id transform-id
                                   :created-by   created-by
                                   :created-at   created-at
                                   :version-name version-name
                                   :is-active?   is-active?}])))
     [:f> version-modal-actions transform {:on-rollback-ev on-rollback-ev}]]))

(defn diff-screen [{:keys [current-version previous-version]}]
  (let [modified @(re-posh/subscribe [:transform/prop [:transform/id current-version] :transform/command])
        original @(re-posh/subscribe [:transform/prop [:transform/id previous-version] :transform/command])]
    [:div {:data-cy "transform-diff-editor"
           :class   "h-full"}
     (when (and (some? original) (some? modified))
       [:f> monaco.diff-editor/diff-editor
        {:original       original
         :modified       modified
         :layout-timeout 100}])]))

(defn transform-version-diff [transform]
  (let [{:keys [transform-id id]} transform
        transform-id (or id transform-id)
        loading? @(re-posh/subscribe [:mold-http/loading? :transform.api/fetch-single-transform])
        {:transform-versions-list/keys [selected]} @(re-posh/subscribe [:transform-versions-list/pull])
        selected-version-id selected]
    [:div {:class "h-full"}
     (cond
       loading?
       [:div {:class "flex justify-center items-center h-full"}
        [c.spinner/inline-configurable {:size 50}]]

       (nil? selected-version-id)
       [:div {:class "flex justify-center items-center h-full"}
        [:p "Please select one of the transform versions from the sidebar"]]

       :otherwise
       [diff-screen
        {:current-version  transform-id
         :previous-version selected-version-id}])]))

(defn revert-circular-dependency-modal []
  (let [{:keys [open? transform]} @(re-posh/subscribe [:transform.subs/revert-circular-dependency-modal-params])
        {:keys [master-id transform-id group-id]} transform
        close #(dispatch [:transform.events/toggle-circular-dependency-modal false])]
    [dialog-root {:open open?
                  :on-open-change (fn [v]
                                    (dispatch [:transform.events/toggle-circular-dependency-modal v]))}
     [dialog-content
      [dialog-title {:class "flex items-center gap-2"}
       [:> TriangleAlert {:class "text-kleene-text-warning"}]
       "Circular dependency detected"]
      [dialog-description "Do you want to revert transform to the last saved version?"]
      [dialog-actions
       [button {:variant  :secondary
                :on-click close}
        "Cancel"]
       [button {:variant  :primary
                :on-click (fn []
                            (dispatch [:transform.events/revert-to-previous-version transform-id master-id group-id]))}
        "Revert"]]]]))

(defn transform-versions [{:keys [transform on-rollback-ev]}]
  [:> Allotment {:class         "h-full"
                 :default-sizes [350 1000]}
   [:> Allotment.Pane {:min-size       300
                       :max-size       600
                       :preferred-size 350}
    [:div {:class "h-full overflow-y-auto"}
     [:f> transform-versions-list transform on-rollback-ev]]]
   [:> Allotment.Pane {:min-size 400}
    [transform-version-diff transform]]])

(defn versions-list-modal []
  (let [{:keys [open? transform on-rollback-ev]} @(re-posh/subscribe [:transform.subs/versions-modal-params])]
    [dialog-root
     {:open           open?
      :on-open-change (fn [_]
                        (dispatch [:transform.events/toggle-version-select-modal false]))}
     [dialog-content
      {:size :full
       :no-padding true
       :class "h-full overflow-hidden flex flex-col"}

      [dialog-title {:class "pb-4"} "Transform versions"]
      [:div {:class "-mx-4 -mb-4 flex-1 border-t"}
       [transform-versions {:transform transform
                            :on-rollback-ev on-rollback-ev}]]]]))


(defn transforms []
  (reagent/create-class
   {:display-name
    "transforms"

    :constructor
    (fn [this]
      (let [group-id  (:group-id (reagent/props this))]
        (when (some? group-id)
          (fetch-transforms group-id true)

          (dispatch [:group.api/fetch-schedule {:group-id group-id}]))))

    :component-did-update
    (fn [this prev-props]
      (let [group-id (:group-id (reagent/props this))
            [_ prev-params] prev-props
            prev-group-id (:group-id prev-params)
            replace? (not= group-id prev-group-id)]
        (when (and (some? group-id) replace?)
          (fetch-transforms group-id replace?)
          (dispatch [:group.api/fetch-schedule {:group-id group-id}]))))

    :render
    (fn []
      [:<>
       [:f> transforms-list]
       [versions-list-modal]
       [revert-circular-dependency-modal]])}))
